import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Test page for Kraken API
 * Displays completely unmodified raw data from the Kraken API
 */
const KrakenTestPage: React.FC = () => {
  const [rawData, setRawData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchRawKrakenData();
  }, []);

  // Function to fetch raw Kraken data directly from Java API
  const fetchRawKrakenData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching completely unmodified raw Kraken data from Java API...');
      
      // For testing, we'll use hardcoded test API keys
      const testApiKey = "YOUR_KRAKEN_API_KEY";
      const testSecretKey = "YOUR_KRAKEN_SECRET_KEY";
      
      console.log('Making direct request to Java API with test credentials...');
      
      // Make a call to the Java REST API, which will return sample data
      const response = await axios.post(`http://localhost:8080/api/kraken/raw-data`, {
        apiKey: testApiKey,
        secretKey: testSecretKey
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Store the completely unmodified raw data
      setRawData(response.data);
      console.log('Raw Kraken data from Java API:', response.data);
    } catch (error: any) {
      console.error('Error fetching raw Kraken data:', error);
      
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        
        setError(`Failed to fetch raw Kraken data: ${error.response?.data?.message || error.message || 'Unknown error'}`);
      } else if (error.request) {
        console.error('Request made but no response received:', error.request);
        setError(`Failed to connect to Java server. Make sure the Java server is running on port 8080.`);
      } else {
        console.error('Error setting up request:', error.message);
        setError(`Error setting up request: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography component="h1" variant="h5">
            Kraken API Test Page
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchRawKrakenData}
            disabled={loading}
          >
            Refresh Data
          </Button>
        </Box>
        
        <Typography variant="body1" gutterBottom>
          This page displays the completely unmodified raw data from the Kraken API for debugging and monitoring purposes.
        </Typography>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}
        
        {rawData && (
          <>
            <Typography variant="h6" gutterBottom>
              Raw Kraken API Response
            </Typography>
            
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6">
                  Completely Unmodified Raw Data from Kraken API
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This displays the exact response from the Kraken API without any transformations
                </Typography>
              </CardContent>
            </Card>
            
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Raw JSON Data</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#272822',
                    color: '#f8f8f2',
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '400px',
                    fontFamily: 'monospace'
                  }}
                >
                  <pre>{JSON.stringify(rawData, null, 2)}</pre>
                </Box>
              </AccordionDetails>
            </Accordion>
            
            {rawData.result && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Balance Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Asset</TableCell>
                          <TableCell>Balance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(rawData.result).map(([asset, balance]) => (
                          <TableRow key={asset}>
                            <TableCell>{asset}</TableCell>
                            <TableCell>{String(balance)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default KrakenTestPage;
